import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { AlertTriangle } from 'lucide-react';
import { confirmLocationUpToDate } from '../helpers/locationStatusApi';
import { formatLocationScopeLabel } from '../helpers/locationScopes';
import { getSavedRequestFeedback } from '../helpers/requestFeedback';
import { locationStatusKey } from '../helpers/adminLocationStatus';
import LocationStatusHistory from './LocationStatusHistory';
import { LocationReviewAction } from './LocationReviewActions';

const statusLabel = (status) => status === 'up_to_date' ? 'Todo está al día' : 'Requiere revisión';

export default function LocationStatusPanel({ accessToken, statuses, loading, error, onRefresh }) {
  const [selectedIndex, setSelectedIndex] = useState('0');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [historyRevision, setHistoryRevision] = useState(0);
  const contextGeneration = useRef(0);
  const selectedStatus = statuses[Number(selectedIndex)];
  const pendingCount = Number(selectedStatus?.pendingRequestsCount || 0);
  const selectedScopeKey = selectedStatus ? locationStatusKey(selectedStatus.locationScope) : '';
  const contextIdentity = `${accessToken}:${selectedScopeKey}`;
  const currentContextIdentity = useRef(contextIdentity);
  if (currentContextIdentity.current !== contextIdentity) {
    currentContextIdentity.current = contextIdentity;
    contextGeneration.current += 1;
  }

  useEffect(() => {
    contextGeneration.current += 1;
    setFeedback(null);
    setSubmitting(false);
    return () => { contextGeneration.current += 1; };
  }, [accessToken, selectedScopeKey]);

  useEffect(() => {
    if (!statuses[Number(selectedIndex)]) setSelectedIndex('0');
  }, [statuses, selectedIndex]);

  async function confirm() {
    if (!selectedStatus || pendingCount || submitting) return;
    const generation = contextGeneration.current;
    setSubmitting(true);
    setFeedback(null);
    try {
      const result = await confirmLocationUpToDate(selectedStatus.locationScope, accessToken);
      if (generation !== contextGeneration.current) return;
      setFeedback(getSavedRequestFeedback(result, 'Se guardó la confirmación: Todo está al día.'));
      setHistoryRevision((previous) => previous + 1);
    } catch (requestError) {
      if (generation !== contextGeneration.current) return;
      setFeedback({ type: 'error', message: requestError.message || 'No se pudo guardar la confirmación.' });
    } finally {
      if (generation === contextGeneration.current) {
        try { await onRefresh(); } catch { /* The parent reports status lookup errors. */ }
        if (generation === contextGeneration.current) setSubmitting(false);
      }
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack spacing={1.25}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Revisión de usuarios de la sede</Typography>
        <Typography variant="body2" color="text.secondary">
          Después de revisar los usuarios, confirma que no se necesitan cambios. Se guardarán la sede, tu nombre y la fecha, y se enviará un correo a los administradores.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {statuses.length > 1 && (
          <TextField
            select
            label="Sede que deseas confirmar"
            value={selectedIndex}
            onChange={(event) => { setSelectedIndex(event.target.value); setFeedback(null); }}
            disabled={loading || submitting}
            fullWidth
          >
            {statuses.map((entry, index) => (
              <MenuItem key={index} value={String(index)}>{formatLocationScopeLabel(entry.locationScope)}</MenuItem>
            ))}
          </TextField>
        )}
        {selectedStatus && (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatLocationScopeLabel(selectedStatus.locationScope)}</Typography>
            <Typography variant="body2" color={selectedStatus.status === 'up_to_date' ? 'success.main' : 'warning.main'}>
              {statusLabel(selectedStatus.status)}
            </Typography>
          </Box>
        )}
        <LocationReviewAction locationLabel={selectedStatus ? formatLocationScopeLabel(selectedStatus.locationScope) : ''}>
          <Stack spacing={1} sx={{ minWidth: 0, maxWidth: { md: 360 } }}>
            <Stack direction="row" alignItems="center" sx={{
              minHeight: 44,
              border: pendingCount > 0 ? '1px solid' : 'none',
              borderColor: 'divider',
              borderRadius: 1.5,
              bgcolor: pendingCount > 0 ? 'action.hover' : 'transparent',
            }}>
              {pendingCount > 0 && <Tooltip
                title={`Esta sede tiene ${pendingCount} solicitud(es) pendiente(s). No se puede confirmar Todo está al día hasta que se hayan resuelto los cambios pendientes.`}
                arrow
                describeChild
              >
                <IconButton aria-label="Hay cambios pendientes que impiden confirmar" color="warning" size="small" sx={{
                  width: 40, height: 42, flexShrink: 0, borderRadius: '6px 0 0 6px',
                  borderRight: '1px solid', borderColor: 'divider',
                }}>
                  <AlertTriangle size={18} />
                </IconButton>
              </Tooltip>}
              <Button variant="contained" color="success" disableElevation onClick={confirm} disabled={loading || submitting || !selectedStatus || pendingCount > 0 || Boolean(error)} sx={{
                whiteSpace: 'nowrap', flex: 1, minHeight: pendingCount > 0 ? 42 : 44, px: 2.5,
                fontWeight: 700, fontSize: '0.8125rem', borderRadius: 1.5,
                ...(pendingCount > 0 && { '&.Mui-disabled': { bgcolor: 'transparent', color: 'text.disabled' } }),
              }}>
                {submitting ? 'Guardando confirmación…' : 'Todo está al día'}
              </Button>
              {loading && <CircularProgress size={20} aria-label="Consultando estado de las sedes" sx={{ mx: 1 }} />}
            </Stack>
            {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}
          </Stack>
        </LocationReviewAction>
        <Typography variant="caption" color="text.secondary">
          La confirmación se guarda automáticamente al pulsar «Todo está al día».
        </Typography>
        {selectedStatus && <LocationStatusHistory accessToken={accessToken} locationScope={selectedStatus.locationScope} revision={historyRevision} />}
      </Stack>
    </Paper>
  );
}
